import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../index';
import { LayoutDashboard, FileSearch, Menu, X, Package, Server, Bot, MessageSquare, DollarSign, Upload, LogOut, Book, Info } from 'lucide-react';

const Sidebar = ({ currentPage, setCurrentPage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { auth } = useContext(AuthContext);
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (auth.isAuthenticated()) {
      console.log('[Sidebar] User authenticated, fetching user info...');
      auth.getUserInfo().then(info => {
        console.log('[Sidebar] User info received:', {
          name: info.name,
          email: info.email,
          groups: info.groups,
          isAdmin: info.isAdmin
        });
        setUserInfo({ name: info.name, email: info.email });
        // Check admin status from auth object (reads groups from ID token)
        const adminStatus = info.isAdmin || false;
        console.log('[Sidebar] Setting admin status:', adminStatus);
        setIsAdmin(adminStatus);
      }).catch(err => {
        console.error('[Sidebar] Failed to get user info:', err);
      });
    } else {
      console.log('[Sidebar] User not authenticated');
      setIsAdmin(false);
    }
  }, [auth]);

  const handleLogout = () => {
    auth.logout();
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 rounded bg-dark-card border border-cyan-accent/40 flex items-center justify-center text-cyan-accent hover:text-white transition-all"
        style={{ 
          boxShadow: '0 0 20px rgba(0, 229, 255, 0.3), 0 0 40px rgba(0, 229, 255, 0.2)',
          background: '#141414'
        }}
      >
        {isMobileMenuOpen ? <X size={20} strokeWidth={2.5} /> : <Menu size={20} strokeWidth={2.5} />}
      </button>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 bg-dark-card border-r border-dark-border flex flex-col relative overflow-hidden
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
           style={{
             background: '#141414',
             boxShadow: '4px 0 40px rgba(0, 0, 0, 0.8), 4px 0 2px rgba(178, 75, 243, 0.1)'
           }}>
        
        {/* Neon glow */}
        <div className="absolute top-20 left-1/2 w-64 h-64 bg-purple-accent opacity-[0.1] blur-[100px] rounded-full pointer-events-none animate-pulse" 
             style={{ animationDuration: '3s' }} />
        
        {/* Logo Section */}
        <div className="p-5 border-b border-dark-border relative z-10">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="absolute inset-0 bg-purple-accent opacity-30 blur-2xl rounded-full" />
              <img 
                src="/vision-logo.png" 
                alt="VISION Logo" 
                className="h-9 w-9 object-contain relative z-10"
                style={{ filter: 'drop-shadow(0 0 15px #b24bf3) drop-shadow(0 0 30px #b24bf3)' }}
              />
            </div>
            <h1 className="text-xl font-bold tracking-wider text-cyan-accent" 
                style={{ textShadow: '0 0 20px #00e5ff, 0 0 40px #00e5ff, 0 0 60px #00e5ff' }}>
              VISION
            </h1>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-3 relative z-10 overflow-y-auto">
          <ul className="space-y-1">
            <li>
              <button
                onClick={() => {
                  setCurrentPage('dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                  currentPage === 'dashboard'
                    ? 'bg-purple-accent bg-opacity-15 text-purple-accent border border-purple-accent/40 hover:bg-opacity-25 hover:border-purple-accent/60'
                    : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                }`}
                style={currentPage === 'dashboard' ? { boxShadow: '0 0 25px rgba(178, 75, 243, 0.3), inset 0 0 15px rgba(178, 75, 243, 0.1)' } : {}}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                <LayoutDashboard size={18} strokeWidth={2.5} style={currentPage === 'dashboard' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                <span className="relative z-10" style={currentPage === 'dashboard' ? { textShadow: '0 0 10px currentColor' } : {}}>Dashboard</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setCurrentPage('chgreview');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                  currentPage === 'chgreview'
                    ? 'bg-orange-accent bg-opacity-15 text-orange-accent border border-orange-accent/40 hover:bg-opacity-25 hover:border-orange-accent/60'
                    : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                }`}
                style={currentPage === 'chgreview' ? { boxShadow: '0 0 25px rgba(255, 107, 53, 0.3), inset 0 0 15px rgba(255, 107, 53, 0.1)' } : {}}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                <FileSearch size={18} strokeWidth={2.5} style={currentPage === 'chgreview' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                <span className="relative z-10" style={currentPage === 'chgreview' ? { textShadow: '0 0 10px currentColor' } : {}}>CHG Review</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setCurrentPage('software');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                  currentPage === 'software'
                    ? 'bg-cyan-accent bg-opacity-15 text-cyan-accent border border-cyan-accent/40 hover:bg-opacity-25 hover:border-cyan-accent/60'
                    : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                }`}
                style={currentPage === 'software' ? { boxShadow: '0 0 25px rgba(0, 229, 255, 0.3), inset 0 0 15px rgba(0, 229, 255, 0.1)' } : {}}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                <Package size={18} strokeWidth={2.5} style={currentPage === 'software' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                <span className="relative z-10" style={currentPage === 'software' ? { textShadow: '0 0 10px currentColor' } : {}}>Software</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setCurrentPage('infrastructure');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                  currentPage === 'infrastructure'
                    ? 'bg-purple-accent bg-opacity-15 text-purple-accent border border-purple-accent/40 hover:bg-opacity-25 hover:border-purple-accent/60'
                    : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                }`}
                style={currentPage === 'infrastructure' ? { boxShadow: '0 0 25px rgba(178, 75, 243, 0.3), inset 0 0 15px rgba(178, 75, 243, 0.1)' } : {}}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                <Server size={18} strokeWidth={2.5} style={currentPage === 'infrastructure' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                <span className="relative z-10" style={currentPage === 'infrastructure' ? { textShadow: '0 0 10px currentColor' } : {}}>Infrastructure</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setCurrentPage('incidentadvisor');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                  currentPage === 'incidentadvisor'
                    ? 'bg-orange-accent bg-opacity-15 text-orange-accent border border-orange-accent/40 hover:bg-opacity-25 hover:border-orange-accent/60'
                    : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                }`}
                style={currentPage === 'incidentadvisor' ? { boxShadow: '0 0 25px rgba(255, 107, 53, 0.3), inset 0 0 15px rgba(255, 107, 53, 0.1)' } : {}}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                <Bot size={18} strokeWidth={2.5} style={currentPage === 'incidentadvisor' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                <span className="relative z-10" style={currentPage === 'incidentadvisor' ? { textShadow: '0 0 10px currentColor' } : {}}>ITOps Advisor</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setCurrentPage('information');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                  currentPage === 'information'
                    ? 'bg-cyan-accent bg-opacity-15 text-cyan-accent border border-cyan-accent/40 hover:bg-opacity-25 hover:border-cyan-accent/60'
                    : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                }`}
                style={currentPage === 'information' ? { boxShadow: '0 0 25px rgba(0, 229, 255, 0.3), inset 0 0 15px rgba(0, 229, 255, 0.1)' } : {}}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                <Info size={18} strokeWidth={2.5} style={currentPage === 'information' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                <span className="relative z-10" style={currentPage === 'information' ? { textShadow: '0 0 10px currentColor' } : {}}>Information</span>
              </button>
            </li>
          </ul>

          {/* Admin Section */}
          {/* ⚠️ SECURITY NOTE: This UI check is for display only. 
              All admin operations must be protected by backend authorization.
              Users can bypass this by modifying client code, but backend will deny access. */}
          {isAdmin && (
          <div className="mt-6 pt-4 border-t border-dark-border/50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2">Admin</p>
            <ul className="space-y-1">
              <li>
                <button
                  onClick={() => {
                    setCurrentPage('financials');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                    currentPage === 'financials'
                      ? 'bg-cyan-accent bg-opacity-15 text-cyan-accent border border-cyan-accent/40 hover:bg-opacity-25 hover:border-cyan-accent/60'
                      : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                  }`}
                  style={currentPage === 'financials' ? { boxShadow: '0 0 25px rgba(0, 229, 255, 0.3), inset 0 0 15px rgba(0, 229, 255, 0.1)' } : {}}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                  <DollarSign size={18} strokeWidth={2.5} style={currentPage === 'financials' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                  <span className="relative z-10" style={currentPage === 'financials' ? { textShadow: '0 0 10px currentColor' } : {}}>Admin Dashboard</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    setCurrentPage('kbupload');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                    currentPage === 'kbupload'
                      ? 'bg-orange-accent bg-opacity-15 text-orange-accent border border-orange-accent/40 hover:bg-opacity-25 hover:border-orange-accent/60'
                      : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                  }`}
                  style={currentPage === 'kbupload' ? { boxShadow: '0 0 25px rgba(255, 107, 53, 0.3), inset 0 0 15px rgba(255, 107, 53, 0.1)' } : {}}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                  <Upload size={18} strokeWidth={2.5} style={currentPage === 'kbupload' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                  <span className="relative z-10" style={currentPage === 'kbupload' ? { textShadow: '0 0 10px currentColor' } : {}}>KB Upload</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    setCurrentPage('prompts');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                    currentPage === 'prompts'
                      ? 'bg-lime-accent bg-opacity-15 text-lime-accent border border-lime-accent/40 hover:bg-opacity-25 hover:border-lime-accent/60'
                      : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                  }`}
                  style={currentPage === 'prompts' ? { boxShadow: '0 0 25px rgba(178, 255, 89, 0.3), inset 0 0 15px rgba(178, 255, 89, 0.1)' } : {}}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-lime-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                  <MessageSquare size={18} strokeWidth={2.5} style={currentPage === 'prompts' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                  <span className="relative z-10" style={currentPage === 'prompts' ? { textShadow: '0 0 10px currentColor' } : {}}>Prompts</span>
                </button>
              </li>

              {/* Documentation */}
              <li className="mt-2">
                <button
                  onClick={() => {
                    setCurrentPage('kbdocs');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold ${
                    currentPage === 'kbdocs'
                      ? 'bg-cyan-accent bg-opacity-15 text-cyan-accent border border-cyan-accent/40'
                      : 'bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-dark-border/40 hover:text-white hover:border-dark-border/60'
                  }`}
                  style={currentPage === 'kbdocs' ? { boxShadow: '0 0 25px rgba(0, 229, 255, 0.3), inset 0 0 15px rgba(0, 229, 255, 0.1)' } : {}}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                  <Book size={18} strokeWidth={2.5} style={currentPage === 'kbdocs' ? { filter: 'drop-shadow(0 0 8px currentColor) drop-shadow(0 0 16px currentColor)' } : {}} />
                  <span className="relative z-10" style={currentPage === 'kbdocs' ? { textShadow: '0 0 10px currentColor' } : {}}>Documentation</span>
                </button>
              </li>
            </ul>
          </div>
          )}
        </nav>

        {/* User Profile Section */}
        <div className="p-3 border-t border-dark-border relative z-10">
          <div className="flex items-center space-x-2.5 px-3 py-2.5 rounded bg-dark-border/40 relative overflow-hidden group hover:bg-dark-border/60 transition-all mb-2">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-accent to-transparent opacity-0 group-hover:opacity-5 transition-opacity" />
            <div className="w-9 h-9 rounded-full bg-cyan-accent flex items-center justify-center text-black text-sm font-bold relative flex-shrink-0"
                 style={{ 
                   boxShadow: '0 0 20px #00e5ff, 0 0 40px #00e5ff',
                   background: '#00e5ff'
                 }}>
              {userInfo.name ? userInfo.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
            </div>
            <div className="flex-1 min-w-0 relative z-10">
              <p className="text-sm font-semibold text-white truncate" style={{ textShadow: '0 0 10px rgba(255,255,255,0.3)' }}>{userInfo.name || userInfo.email || 'User'}</p>
              <p className="text-xs text-gray-500">{userInfo.email || 'Administrator'}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-3 py-2 rounded transition-all relative overflow-hidden group text-sm font-semibold bg-dark-border/20 text-gray-400 border border-dark-border/40 hover:bg-orange-accent/10 hover:text-orange-accent hover:border-orange-accent/40"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-orange-accent to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
            <LogOut size={18} strokeWidth={2.5} />
            <span className="relative z-10">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;

